import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useApi } from '../hooks/useApi';

/**
 * InstancesOverviewPage - read-only Betriebs-Uebersicht der laufenden Versionen je
 * p4i/p4a-Instanz. Das Backend (GET /api/platform/admin/instances/health) ruft
 * server-seitig das /api/health jeder konfigurierten Instanz ab (DEV/STG/PROD/DEMO)
 * und liefert On-Prem-Deployments aus dem Heartbeat (nicht outbound abrufbar).
 * platform_admin-gated.
 */
const ENV_ORDER = { prod: 0, staging: 1, demo: 2, dev: 3, onprem: 4 };

function dot(ok) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: ok ? '#10b981' : '#ef4444', marginRight: 6,
    }} />
  );
}

function Th({ children }) {
  return <th style={{ textAlign: 'left', fontSize: 11, textTransform: 'uppercase',
    letterSpacing: '.5px', color: 'var(--text-muted)', padding: '6px 10px' }}>{children}</th>;
}
function Td({ children, mono }) {
  return <td style={{ fontSize: 13, padding: '6px 10px', color: 'var(--text)',
    fontFamily: mono ? 'monospace' : undefined }}>{children}</td>;
}

export default function InstancesOverviewPage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api(`/admin/instances/health${includeRevoked ? '?include_revoked=true' : ''}`);
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [api, includeRevoked]);

  useEffect(() => { load(); }, [load]);

  const instances = (data?.instances || []).slice().sort(
    (a, b) => (a.tool).localeCompare(b.tool) || (ENV_ORDER[a.env] ?? 9) - (ENV_ORDER[b.env] ?? 9));
  const onprem = data?.onprem || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>
          {t('instances.title', 'Instanzen und Versionen')}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          {loading ? t('common.loading', 'Lade...') : t('instances.refresh', 'Aktualisieren')}
        </button>
        {data?.checked_at && (
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {t('instances.checked_at', 'Geprueft')}: {new Date(data.checked_at).toLocaleString()}
          </span>
        )}
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
          borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: 'var(--danger, #ef4444)', marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-surface)' }}>
            <tr>
              <Th>{t('instances.tool', 'Tool')}</Th>
              <Th>{t('instances.env', 'Umgebung')}</Th>
              <Th>{t('instances.version', 'Version')}</Th>
              <Th>{t('instances.status', 'Status')}</Th>
              <Th>{t('instances.reachable', 'Erreichbar')}</Th>
              <Th>URL</Th>
            </tr>
          </thead>
          <tbody>
            {instances.map((i) => (
              <tr key={`${i.tool}-${i.env}`} style={{ borderTop: '1px solid var(--border)' }}>
                <Td>{i.tool}</Td>
                <Td>{i.env}</Td>
                <Td mono>{i.version || '-'}</Td>
                <Td>{i.status || '-'}</Td>
                <Td>{dot(i.reachable)}{i.reachable ? 'ok' : (i.error || 'nein')}</Td>
                <Td mono>{i.url}</Td>
              </tr>
            ))}
            {instances.length === 0 && !loading && (
              <tr><Td>{t('instances.none', 'Keine Instanzen konfiguriert.')}</Td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {t('instances.onprem_title', 'On-Prem-Deployments (via Heartbeat)')}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
          color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
          />
          {t('instances.show_revoked', 'Widerrufene (revoked) anzeigen')}
        </label>
        {!includeRevoked && data?.revoked_hidden > 0 && (
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {data.revoked_hidden} {t('instances.revoked_hidden_suffix', 'widerrufene ausgeblendet')}
          </span>
        )}
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-surface)' }}>
            <tr>
              <Th>{t('instances.tool', 'Tool')}</Th>
              <Th>Host</Th>
              <Th>{t('instances.version', 'Version')}</Th>
              <Th>{t('instances.deployment_status', 'Deployment-Status')}</Th>
              <Th>{t('instances.last_seen', 'Zuletzt gesehen')}</Th>
            </tr>
          </thead>
          <tbody>
            {onprem.map((d) => (
              <tr key={`${d.tool}-${d.instance_id}`} style={{ borderTop: '1px solid var(--border)' }}>
                <Td>{d.tool}</Td>
                <Td>{d.host || '-'}</Td>
                <Td mono>{d.version || t('instances.not_reported', '(nicht gemeldet)')}</Td>
                <Td>{d.revoked ? 'revoked' : (d.deployment_status || '-')}</Td>
                <Td>{d.last_seen ? new Date(d.last_seen).toLocaleString() : t('instances.never', 'nie')}</Td>
              </tr>
            ))}
            {onprem.length === 0 && (
              <tr><Td>{t('instances.no_onprem', 'Keine On-Prem-Deployments.')}</Td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
